import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import AuthHeader from '../../components/AuthHeader'
import toast from 'react-hot-toast'

const SignUpPage = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    username: '',
    password: '',
    termsAccepted: false
  })
  const [loading, setLoading] = useState(false)
  const { register } = useAuth()
  const navigate = useNavigate()

  // Check if all required fields are filled
  const isFormValid =
    formData.firstName.trim() !== '' &&
    formData.lastName.trim() !== '' &&
    formData.email.trim() !== '' &&
    formData.username.trim() !== '' &&
    formData.password.trim() !== '' &&
    formData.termsAccepted;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
 

    if (!formData.termsAccepted) {
      toast.error('Please accept the terms and conditions')
      return
    }

    setLoading(true)

    try {
      const result = await register(formData)
      
      if (result.success) {
        if (result.requiresEmailVerification) {
          toast.success(result.message || 'Account created! Please check your email to verify your account.')
          navigate('/verify-email', { 
            state: { 
              email: formData.email,
              message: result.message 
            } 
          })
        } else {
          toast.success('Account created successfully!')
          navigate('/')
        }
      } else {
        toast.error(result.error)
      }
    } catch (error) {
      toast.error('An unexpected error occurred')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-black">
      <div className='p-4'>
        <div className="border-2 border-white">
          <AuthHeader />
          <div className='bg-[#141414] p-4 w-full min-h-screen flex items-center justify-center'>
            <div className="w-full max-w-sm relative">
       

        {/* Form Card */}
        <div className="bg-card border border-gray-700  p-6 relative">
          {/* Tab Navigation */}
          <div className="flex border-b border-gray-700 mb-6 pb-2">
            <div className="flex-1 py-2 px-4 text-center text-xs font-semibold text-white border-b-2 border-red-600">
              SIGN UP
            </div>
            <Link 
              to="/login" 
              className="flex-1 py-2 px-4 text-center text-xs font-medium text-gray-400 hover:text-white transition-colors"
            >
              LOG IN
            </Link>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5 flex flex-col">

            <div className='flex justify-center items-center !mb-4'>
              <h4>
                Create A TLP Account
              </h4>
            </div>
            {/* First Name & Last Name Row */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
               
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  className="w-full h-10 px-3 py-2 bg-white border-0  text-sm text-gray-900 placeholder:text-center placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all font-avenir"
                  placeholder="First Name"
                  required
                  disabled={loading}
                />
              </div>

              <div className="space-y-1">
             
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  className="w-full h-10 px-3 py-2 bg-white border-0  text-sm text-gray-900 placeholder:text-center placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all font-avenir"
                  placeholder="Last Name"
                  required
                  disabled={loading}
                />
              </div>
            </div>

            {/* Email Row */}
            <div className="space-y-1">
             
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full h-10 px-3 py-2 bg-white border-0  text-sm text-gray-900 placeholder:text-center placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all font-avenir"
                placeholder="Email Address"
                required
                disabled={loading}
              />
            </div>

            {/* Username Row */}
            <div className="space-y-1 !mt-6">
             
              <input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleChange}
                className="w-full h-10 px-3 py-2 bg-white border-0  text-sm text-gray-900 placeholder:text-center placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all font-avenir"
                placeholder="User Name"
                required
                disabled={loading}
              />
            </div>

            {/* Password Field */}
            <div className="space-y-1">
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className="w-full h-10 px-3 py-2 bg-white border-0 text-sm text-gray-900 placeholder:text-center placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all font-avenir"
                placeholder="Password"
                required
                minLength={6}
                disabled={loading}
              />
            </div>

            {/* Terms Checkbox */}
            <div className="space-y-1">
              <label className="flex items-start gap-3 cursor-pointer !p-2">
                <input
                  type="checkbox"
                  name="termsAccepted"
                  checked={formData.termsAccepted}
                  onChange={handleChange}
                  className="mt-0.5 w-4 h-4 text-red-600 bg-white border-gray-300  focus:ring-red-500 focus:ring-2 flex-shrink-0"
                  required
                  disabled={loading}
                />
                <span className="text-xs text-gray-300 leading-relaxed font-avenir">
                  I Agree The{' '}
                  <Link 
                    to="/terms-of-use" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-red-400 hover:text-red-300 transition-colors underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Terms and Conditions
                  </Link>
                </span>
              </label>
            </div>

            <div className='!mx-6'>
              <button
                type="submit"
                disabled={loading || !isFormValid}
                className="w-full h-10 bg-red-600 hover:bg-red-700 disabled:bg-red-700 disabled:cursor-not-allowed text-white font-bold text-sm rounded-lg transition-colors flex items-center justify-center uppercase tracking-wide font-avenir"
              >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Creating...</span>
                </div>
              ) : (
                'REGISTER'
              )}
              </button>
            </div>
          </form>

          {/* Footer Link */}
          <div className="mt-6 text-center">
            <p className="text-xs text-gray-400 font-avenir">
              Already Have An Account?{' '}
              <Link 
                to="/login" 
                className="text-red-400 hover:text-red-300 transition-colors font-medium font-avenir"
              >
                Sign In
              </Link>
            </p>
          </div>

          {/* Footer Links */}
          <div className="mt-6 pt-4 border-t border-gray-700 flex flex-wrap justify-center gap-3 text-xs">
            <Link
              to="/privacy-policy"
              className="text-gray-400 hover:text-red-300 transition-colors font-avenir"
            >
              Privacy Policy
            </Link>
            <span className="text-gray-600">|</span>
            <Link
              to="/terms-of-use"
              className="text-gray-400 hover:text-red-300 transition-colors font-avenir"
            >
              Terms of Use
            </Link>
          </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>

  )
}

export default SignUpPage